/*
 * Bloop: smooth, carousel-style two-finger trackpad swipe through product
 * images.
 *
 * Dawn's <media-gallery> element (media-gallery.js) already knows how to
 * switch the active image via setActiveMedia(mediaId, prepend) - clicking a
 * thumbnail just calls that. That method only toggles the "is-active" class
 * though, with nothing to animate between: our product pages use
 * gallery_layout "thumbnail", so at desktop widths section-main-product.css
 * hides every media item except the active one outright (display: none).
 * That's fine for a click, but it's why a trackpad swipe just "jumped" to
 * the next photo instead of sliding into it.
 *
 * This still listens for the wheel events a two-finger trackpad swipe fires
 * (a wheel event with a dominant deltaX) on the gallery viewer, but instead
 * of switching the active image the moment a swipe is detected, it
 * live-tracks the gesture: the current image slides out under the user's
 * fingers while the next/previous one - temporarily revealed and absolutely
 * positioned via the .is-swiping/.is-swipe-target classes added in
 * bloop-overrides.css - slides in from the side. When the gesture ends,
 * whichever side has moved far enough finishes the slide and calls
 * setActiveMedia; otherwise both snap back to where they started.
 *
 * Only engages where Dawn is actually hiding inactive items, checked via
 * computed style rather than a hardcoded breakpoint/layout check, so it
 * naturally turns itself off wherever that's not the case - e.g. narrower
 * breakpoints where the gallery becomes a native horizontally-scrolling
 * slider and two-finger scrolling should just scroll it normally.
 *
 * Respects prefers-reduced-motion: no sliding, just the original debounced
 * instant swap based on the net direction of the gesture.
 *
 * Direction convention: positive deltaX (the same "swipe left to see what's
 * next" gesture as a normal horizontal scroll) steps forward, so the active
 * slide moves by -offset (left, out of view) while the incoming slide moves
 * by direction*viewerWidth - offset (starting just off-screen on the side
 * travel is heading towards, sliding in to 0).
 *
 * Note: the viewer also contains a zoom-toggle button per media item that
 * carries its own [data-media-id] attribute (the raw numeric id, vs. the
 * slide's "template--...__main-<id>" form), so we scope our query to
 * ".product__media-item[data-media-id]" to only ever see the actual slides.
 */
document.addEventListener('DOMContentLoaded', function () {
  document.querySelectorAll('media-gallery').forEach(initBloopGallerySwipe);
});

function initBloopGallerySwipe(gallery) {
  if (typeof gallery.setActiveMedia !== 'function') return;

  var viewer = gallery.querySelector('[id^="GalleryViewer"]');
  if (!viewer) return;

  var list = viewer.querySelector('.product__media-list');
  if (!list) return;

  var MIN_DELTA = 12;
  var GESTURE_END_MS = 120;
  var COOLDOWN_MS = 200;
  var SETTLE_MS = 220;
  var COMMIT_THRESHOLD = 0.22; // fraction of viewer width

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var cooling = false;
  var dragging = false;
  var endTimer = null;
  var settleTimer = null;

  var items = [];
  var activeIndex = -1;
  var activeItem = null;
  var targetItem = null;
  var targetIndex = -1;
  var direction = 0;
  var offset = 0;
  var viewerWidth = 0;

  viewer.addEventListener(
    'wheel',
    function (event) {
      var deltaX = event.deltaX;
      var deltaY = event.deltaY;

      if (!dragging) {
        if (Math.abs(deltaX) < MIN_DELTA || Math.abs(deltaX) <= Math.abs(deltaY)) {
          return;
        }
        if (cooling) {
          event.preventDefault();
          return;
        }
        if (!galleryIsHidingInactiveItems()) {
          // Not in a layout/breakpoint where items are display:none'd -
          // leave the gesture alone and let it behave as a normal scroll.
          return;
        }
        if (!startDrag()) return;
      }

      event.preventDefault();
      updateDrag(deltaX);
      scheduleEndCheck();
    },
    { passive: false }
  );

  function galleryIsHidingInactiveItems() {
    var inactive = list.querySelector('.product__media-item[data-media-id]:not(.is-active)');
    if (!inactive) return false;
    return window.getComputedStyle(inactive).display === 'none';
  }

  function startDrag() {
    items = Array.prototype.slice.call(
      viewer.querySelectorAll('.product__media-item[data-media-id]')
    );
    if (items.length < 2) return false;

    activeIndex = items.findIndex(function (item) {
      return item.classList.contains('is-active');
    });
    if (activeIndex === -1) return false;

    activeItem = items[activeIndex];
    viewerWidth = viewer.getBoundingClientRect().width || activeItem.offsetWidth;
    if (!viewerWidth) return false;

    targetItem = null;
    targetIndex = -1;
    direction = 0;
    offset = 0;
    dragging = true;

    if (!reduceMotion) {
      list.classList.add('is-swiping');
      activeItem.style.transition = 'none';
    }

    return true;
  }

  function setTarget(newDirection) {
    if (newDirection === direction && targetItem) return;

    if (targetItem) {
      targetItem.classList.remove('is-swipe-target');
      targetItem.style.transition = '';
      targetItem.style.transform = '';
    }

    direction = newDirection;

    var newIndex = activeIndex + newDirection;
    if (newIndex < 0 || newIndex >= items.length) {
      targetItem = null;
      targetIndex = -1;
      return;
    }

    targetItem = items[newIndex];
    targetIndex = newIndex;
    targetItem.classList.add('is-swipe-target');
    targetItem.style.transition = 'none';
  }

  function updateDrag(deltaX) {
    offset += deltaX;

    if (reduceMotion) return;

    var newDirection = offset > 0 ? 1 : offset < 0 ? -1 : direction;
    if (newDirection !== 0 && newDirection !== direction) {
      setTarget(newDirection);
    }

    // No neighbor to reveal in this direction - clamp at the boundary.
    if (!targetItem) {
      offset = 0;
    } else if (Math.abs(offset) > viewerWidth) {
      offset = viewerWidth * (offset > 0 ? 1 : -1);
    }

    render();
  }

  function render() {
    activeItem.style.transform = 'translateX(' + -offset + 'px)';
    if (targetItem) {
      var targetOffset = direction * viewerWidth - offset;
      targetItem.style.transform = 'translateX(' + targetOffset + 'px)';
    }
  }

  function scheduleEndCheck() {
    if (endTimer) window.clearTimeout(endTimer);
    endTimer = window.setTimeout(endDrag, GESTURE_END_MS);
  }

  function endDrag() {
    endTimer = null;
    if (!dragging) return;
    dragging = false;

    if (reduceMotion) {
      finishReducedMotion();
      return;
    }

    var shouldCommit = !!targetItem && Math.abs(offset) >= viewerWidth * COMMIT_THRESHOLD;

    var finalActiveOffset = shouldCommit ? -direction * viewerWidth : 0;
    var settledTarget = targetItem;
    var finalTargetOffset = shouldCommit ? 0 : direction * viewerWidth;

    var doneActiveItem = activeItem;
    var doneTargetMediaId = settledTarget ? settledTarget.getAttribute('data-media-id') : null;

    doneActiveItem.style.transition = 'transform ' + SETTLE_MS + 'ms ease';
    doneActiveItem.style.transform = 'translateX(' + finalActiveOffset + 'px)';

    if (settledTarget) {
      settledTarget.style.transition = 'transform ' + SETTLE_MS + 'ms ease';
      settledTarget.style.transform = 'translateX(' + finalTargetOffset + 'px)';
    }

    cooling = true;
    if (settleTimer) window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(function () {
      cleanup(doneActiveItem, settledTarget);

      if (shouldCommit && doneTargetMediaId) {
        gallery.setActiveMedia(doneTargetMediaId, false);
      }

      window.setTimeout(function () {
        cooling = false;
      }, COOLDOWN_MS);
    }, SETTLE_MS + 20);
  }

  function cleanup(doneActiveItem, settledTarget) {
    list.classList.remove('is-swiping');

    doneActiveItem.style.transition = '';
    doneActiveItem.style.transform = '';

    if (settledTarget) {
      settledTarget.classList.remove('is-swipe-target');
      settledTarget.style.transition = '';
      settledTarget.style.transform = '';
    }

    activeItem = null;
    targetItem = null;
    targetIndex = -1;
    direction = 0;
    offset = 0;
  }

  function finishReducedMotion() {
    // No live tracking happened - fall back to the original instant swap,
    // based on the net direction of the whole gesture.
    var netOffset = offset;
    offset = 0;
    if (netOffset === 0) return;

    var dir = netOffset > 0 ? 1 : -1;
    var nextIndex = activeIndex + dir;
    if (nextIndex < 0 || nextIndex >= items.length) return;

    var nextMediaId = items[nextIndex].getAttribute('data-media-id');
    gallery.setActiveMedia(nextMediaId, false);

    cooling = true;
    window.setTimeout(function () {
      cooling = false;
    }, COOLDOWN_MS);
  }
}
